/**
 * Web override for AttachmentCarouselModal.
 * Adds: keyboard ←/→ navigation, arrow buttons, dot indicators, thumbnail filmstrip.
 */
import React, { useRef, useCallback, useEffect } from 'react';
import {
  Modal,
  View,
  FlatList,
  StyleSheet,
  LayoutChangeEvent,
  ViewToken,
  ActivityIndicator,
} from 'react-native';

import { AttachmentCarouselModalProps } from './AttachmentCarouselModal';
import { CarouselFooter } from './CarouselFooter';
import { CarouselHeader } from './CarouselHeader';
import { CarouselItem } from './CarouselItem';
import { CarouselNavArrow } from './CarouselNavArrow';
import { requestAttachmentCarouselSource } from './requestAttachmentCarouselSource';
import { useCarouselLazyLoad } from './useCarouselLazyLoad';
import { useCarouselState } from './useCarouselState';
import { useResponsive } from '../../../hooks/useResponsive';

import type { CarouselMedia } from './carouselMedia';

export const AttachmentCarouselModal: React.FC<
  AttachmentCarouselModalProps
> = ({
  visible,
  onClose,
  items,
  initialIndex,
  sectionLabel,
  source = requestAttachmentCarouselSource,
}) => {
  const { dimensions } = useResponsive();
  const screenWidth = dimensions.width;

  const { currentIndex, setCurrentIndex, flatListRef, getItemLayout } =
    useCarouselState({ items, initialIndex, visible, screenWidth });

  // Suppress onViewableItemsChanged during programmatic scrollToIndex so
  // intermediate page visibility events don't make the footer jump around.
  const isProgrammaticScroll = useRef(false);
  const programmaticScrollTimer = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const [viewerHeight, setViewerHeight] = React.useState(() => {
    // Initial estimate; will be corrected by onLayout
    return Math.max(dimensions.height - 120, 200);
  });
  const [layoutReady, setLayoutReady] = React.useState(false);

  const onViewerLayout = useCallback((e: LayoutChangeEvent) => {
    const h = e.nativeEvent.layout.height;
    if (h > 0) {
      setViewerHeight(h);
      setLayoutReady(true);
    }
  }, []);

  const { getFileUrl, isLoading, hasError } = useCarouselLazyLoad(
    items,
    currentIndex,
    source,
  );

  // Web-specific scroll with programmatic scroll suppression
  const scrollTo = useCallback(
    (index: number) => {
      const clamped = Math.max(0, Math.min(index, items.length - 1));
      isProgrammaticScroll.current = true;
      if (programmaticScrollTimer.current)
        clearTimeout(programmaticScrollTimer.current);
      programmaticScrollTimer.current = setTimeout(() => {
        isProgrammaticScroll.current = false;
      }, 500);
      setCurrentIndex(clamped);
      flatListRef.current?.scrollToIndex({ index: clamped, animated: true });
    },
    [items.length, setCurrentIndex, flatListRef],
  );

  // Cleanup programmaticScrollTimer on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (programmaticScrollTimer.current) {
        clearTimeout(programmaticScrollTimer.current);
      }
    };
  }, []);

  // Keyboard navigation
  useEffect(() => {
    if (!visible) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') scrollTo(currentIndex - 1);
      if (e.key === 'ArrowRight') scrollTo(currentIndex + 1);
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, [visible, currentIndex, scrollTo, onClose]);

  const onViewableItemsChanged = useCallback(
    ({ viewableItems }: { viewableItems: ViewToken[] }) => {
      if (isProgrammaticScroll.current) return;
      if (viewableItems.length > 0 && viewableItems[0].index !== null) {
        setCurrentIndex(viewableItems[0].index);
      }
    },
    [setCurrentIndex],
  );

  const viewabilityConfig = useRef({
    viewAreaCoveragePercentThreshold: 50,
  }).current;

  const renderItem = useCallback(
    ({ item, index }: { item: CarouselMedia; index: number }) => (
      <CarouselItem
        item={item}
        fileUrl={getFileUrl(item.id)}
        isLoading={isLoading(item.id)}
        hasError={hasError(item.id)}
        isActive={index === currentIndex}
        height={viewerHeight}
        onDownload={source.download ? () => source.download!(item) : undefined}
      />
    ),
    [getFileUrl, isLoading, hasError, currentIndex, viewerHeight, source],
  );

  if (!visible || items.length === 0) return null;

  return (
    <Modal visible={visible} animationType="fade" onRequestClose={onClose}>
      <View style={styles.container} testID="carousel-modal">
        <CarouselHeader
          sectionLabel={items[currentIndex]?.sectionLabel || sectionLabel}
          fileName={items[currentIndex]?.name}
          currentIndex={currentIndex}
          total={items.length}
          onClose={onClose}
        />

        <View
          style={styles.viewerArea}
          onLayout={onViewerLayout}
          testID="carousel-viewer-area"
        >
          {!layoutReady && (
            <View style={styles.loadingSkeleton}>
              <ActivityIndicator size="large" color="#fff" />
            </View>
          )}
          {layoutReady && (
            <>
              <FlatList
                ref={flatListRef}
                data={items}
                renderItem={renderItem}
                keyExtractor={item => item.id}
                horizontal
                pagingEnabled
                showsHorizontalScrollIndicator={false}
                getItemLayout={getItemLayout}
                onViewableItemsChanged={onViewableItemsChanged}
                viewabilityConfig={viewabilityConfig}
                initialScrollIndex={initialIndex}
                windowSize={3}
                maxToRenderPerBatch={3}
              />

              {items.length > 1 && (
                <>
                  <CarouselNavArrow
                    direction="left"
                    onPress={() => scrollTo(currentIndex - 1)}
                    disabled={currentIndex === 0}
                  />
                  <CarouselNavArrow
                    direction="right"
                    onPress={() => scrollTo(currentIndex + 1)}
                    disabled={currentIndex === items.length - 1}
                  />
                </>
              )}
            </>
          )}
        </View>

        {items.length > 1 && (
          <CarouselFooter
            items={items}
            currentIndex={currentIndex}
            onSelect={scrollTo}
            source={source}
          />
        )}
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.1)',
    // On mobile browsers the viewport origin sits behind the address bar.
    // paddingTop is overridden below with a web-only env() value via inline style.
  },
  viewerArea: {
    flex: 1,
    position: 'relative',
  },
  loadingSkeleton: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#111',
  },
});
